import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AccountIdSchema,
  AnyPresentableResourceSchema,
  ErrorGenericSchema,
  GenericResourceId,
  mcpExposeMetadata,
  PresentableTaskResourceSchema,
  ResourceBrowseQueryParamsSchema,
  ResourceQueryParamsSchema,
  ResourceTypeSchema,
  ResponseMetaSchema,
} from '../models';

extendZodWithOpenApi(z);

const c = initContract();

export const apiResources = c.router({
  getResource: {
    method: 'GET',
    path: `/1.0/resources/byId/:resourceId`,
    pathParams: z.object({ resourceId: GenericResourceId }),
    responses: {
      200: AnyPresentableResourceSchema,
    },
    summary: 'Get a single resource by its ID',
    description:
      'Retrieves a single resource by its unique identifier. Returns the full resource details including type, provider, metadata, and associations.',
    metadata: mcpExposeMetadata,
  },
  browseResources: {
    method: 'GET',
    path: `/1.0/resources/browse`,
    query: ResourceBrowseQueryParamsSchema,
    responses: {
      200: z.object({
        resources: z.array(AnyPresentableResourceSchema),
        meta: ResponseMetaSchema.pick({ count: true }).extend({
          hasMore: z
            .boolean()
            .describe(
              'Whether there are more results available (derived via limit+1; avoids expensive total counts).'
            ),
        }),
      }),
    },
    summary: 'Browse imported resources for a connection',
    description:
      'Browse resources imported by a specific connection with filtering, ordering, and search. Intended for admin/inspector-style deep browsing and contextualization. Note: response is intentionally anchored to the existing `AnyPresentableResource` read model for now; future expansions may add explicit context/association views (e.g. `/details`) once the admin browser needs stabilize.',
    metadata: mcpExposeMetadata,
  },
  getResourcesByType: {
    method: 'GET',
    path: `/1.0/resources/type/:resourceType`,
    pathParams: z.object({
      resourceType: z
        .union([ResourceTypeSchema, z.literal('all')])
        .optional()
        .default('all')
        .describe('The type of resource to fetch. Defaults to `all` if not provided.'),
    }),
    query: ResourceQueryParamsSchema.omit({ resourceType: true }),
    responses: {
      200: z.object({
        resources: z.array(AnyPresentableResourceSchema),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true }),
      }),
    },
    summary: 'Get resources filtered by type',
    description:
      'Retrieves a list of resources filtered by their type (e.g., project, organization, task). Supports pagination and additional query filters. Use "all" to retrieve resources of all types.',
    metadata: mcpExposeMetadata,
  },
  getTasks: {
    method: 'GET',
    path: `/1.0/resources/tasks`,
    query: z.object({
      accountId: AccountIdSchema,
      search: z.string().optional().describe('Text search query'),
      toolId: z.string().optional().describe('Filter by tool ID'),
      projectUri: z
        .string()
        .optional()
        .describe(
          "Filter to tasks carrying this project's TIC URI in `metadata.project.uri` (TT-5457)"
        ),
      status: z
        .enum(['open', 'done', 'none'])
        .optional()
        .describe(
          'Filter by completion state: `open` = not completed, `done` = completed, `none` = nothing ' +
            'matches (every status de-selected in the UI). Omit for both. Applied in the query, ' +
            'BEFORE pagination — a client-side filter after pagination hides rows the server would ' +
            'have returned and makes later pages unreachable (TT-5457).'
        ),
      includeDeleted: z.coerce
        .boolean()
        .optional()
        .default(false)
        .describe('Include soft-deleted tasks in results'),
      limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(250)
        .optional()
        .default(50)
        .describe('Maximum number of results to return per page (1-250, default: 50)'),
      page: z.coerce
        .number()
        .int()
        .min(1)
        .optional()
        .default(1)
        .describe('Page number for pagination (default: 1)'),
    }),
    responses: {
      200: z.object({
        tasks: z.array(PresentableTaskResourceSchema),
        meta: ResponseMetaSchema.pick({
          count: true,
          // Removing this due to extreme cost for per-now unused feature
          // ref https://github.com/Timely/Frontend/pull/5071#issuecomment-2605257087
          // totalCount: true
        }),
      }),
      504: ErrorGenericSchema.describe(
        'The task query exceeded its statement timeout and was canceled server-side. ' +
          'Carries `Retry-After`. Retrying the identical request usually reproduces it, so a ' +
          'client should surface the failure rather than retry automatically.'
      ),
    },
    summary: 'Get "external tasks" for Timelys `HourEditor` to add `external_id` reference',
    description:
      'Retrieves task-type resources from connected integrations for use in the Timely HourEditor. These tasks can be linked to time entries via the `external_id` field, enabling cross-referencing between Timely hours and external task management systems.',
    metadata: mcpExposeMetadata,
  },
  searchTasks: {
    method: 'POST',
    path: `/1.0/resources/tasks/search`,
    body: z.object({
      accountId: AccountIdSchema,
      ids: z.array(z.string()).optional().describe('Filter to specific resource IDs'),
      uris: z.array(z.string()).optional().describe('Filter to specific resource URIs'),
      search: z.string().optional().describe('Text search query'),
      toolId: z.string().optional().describe('Filter by tool ID'),
      includeDeleted: z
        .boolean()
        .optional()
        .default(false)
        .describe('Include soft-deleted tasks in results'),
      limit: z.number().int().min(1).max(250).optional().default(50),
      page: z.number().int().min(1).optional().default(1),
    }),
    responses: {
      200: z.object({
        tasks: z.array(PresentableTaskResourceSchema),
        meta: ResponseMetaSchema.pick({ count: true }),
      }),
      504: ErrorGenericSchema.describe(
        'The task query exceeded its statement timeout and was canceled server-side. ' +
          'Carries `Retry-After`. Retrying the identical request usually reproduces it, so a ' +
          'client should surface the failure rather than retry automatically.'
      ),
    },
    summary: 'Search tasks by IDs, URIs, or text query',
    description:
      'Searches for task-type resources across connected integrations. Supports filtering by specific IDs, URIs, text search, or tool. Returns paginated results with task metadata for display in the Timely UI.',
  },
});
